<template>
  <div class="dashboard">
    <el-card class="status-card">
      <template #header>
        <div class="card-header">
          <h3>队列状态</h3>
          <div class="refresh-container">
            <span class="countdown">{{ refreshCountdown }}s</span>
            <el-button type="primary" size="small" @click="manualRefresh">刷新</el-button>
          </div>
        </div>
      </template>
      <el-tabs v-model="activeQueue">
        <el-tab-pane v-for="(status, queue) in queueStatus" :key="queue" :label="queue" :name="queue">
          <el-descriptions border :column="4" size="small">
            <el-descriptions-item label="当前任务数" :span="2" class="current-task-item">
              <el-tooltip
                effect="dark"
                :content="getCurrentTasksTooltip(status.all_processing_tasks)"
                placement="top"
                :disabled="!status.all_processing_tasks || status.all_processing_tasks.length === 0"
              >
                <span>{{ status.all_processing_tasks?.length || 0 }}</span>
              </el-tooltip>
            </el-descriptions-item>
            <el-descriptions-item label="总任务数">
              {{ status.total_tasks }}
            </el-descriptions-item>
            <el-descriptions-item label="队列中任务">
              {{ status.all_queued_tasks?.length || 0 }}
            </el-descriptions-item>
            <el-descriptions-item label="连接状态">
              <el-tooltip
                effect="dark"
                :content="getStatusTooltip(status)"
                placement="top"
              >
                <el-tag :type="getStatusType(status.status)" :color="getStatusColor(status.status)" :style="{color: getStatusTextColor(status.status)}">
                  {{ getStatusText(status.status) }}
                </el-tag>
              </el-tooltip>
            </el-descriptions-item>
            <el-descriptions-item label="解析功能状态" :span="2">
              <el-tooltip
                effect="dark"
                :content="getParseStatusTooltip(status)"
                placement="top"
              >
                <el-tag :type="getParseStatusType(status.parse_status)" :color="getParseStatusColor(status.parse_status)" :style="{color: getParseStatusTextColor(status.parse_status)}">
                  {{ getParseStatusText(status.parse_status) }}
                </el-tag>
              </el-tooltip>
            </el-descriptions-item>
            <el-descriptions-item label="操作">
              <el-button
                type="primary"
                size="small"
                @click="testParseFunction(queue)"
                :loading="testingQueues.includes(queue)"
                :disabled="testingQueues.includes(queue) || cooldownQueues?.[queue]"
              >
                {{ testingQueues.includes(queue) ? '测试中...' : '测试解析功能' }}
              </el-button>
            </el-descriptions-item>
          </el-descriptions>

          <!-- 队列任务详情折叠面板 -->
          <el-collapse v-if="status.all_queued_tasks && status.all_queued_tasks.length > 0" style="margin-top: 16px;">
            <el-collapse-item title="队列中的任务" :name="queue + '_queued'">
              <el-table :data="status.all_queued_tasks" stripe style="width: 100%">
                <el-table-column label="任务名称">
                  <template #default="scope">
                    {{ scope.row }}
                  </template>
                </el-table-column>
              </el-table>
            </el-collapse-item>
          </el-collapse>

          <el-collapse v-if="status.all_processing_tasks && status.all_processing_tasks.length > 0" style="margin-top: 16px;">
            <el-collapse-item title="正在处理的任务" :name="queue + '_processing'">
              <el-table :data="status.all_processing_tasks" stripe style="width: 100%">
                <el-table-column label="任务名称">
                  <template #default="scope">
                    {{ scope.row }}
                  </template>
                </el-table-column>
              </el-table>
            </el-collapse-item>
          </el-collapse>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 消息提示 -->
    <el-alert
      v-if="message"
      :title="message"
      type="info"
      :closable="true"
      @close="message = ''"
      style="margin-bottom: 20px;"
    />
  </div>
</template>

<script>
export default {
  data() {
    return {
      queueStatus: {},
      activeQueue: 'demo',
      refreshCountdown: 30, // 倒计时初始值
      refreshInterval: 30, // 刷新间隔，秒
      countdownTimer: null,
      testingQueues: [], // 正在测试的队列
      cooldownQueues: {}, // 冷却中的队列
      message: '' // 消息提示
    }
  },
  computed: {
    queueList() {
      return Object.keys(this.queueStatus);
    }
  },
  methods: {
    async refreshData() {
      try {
        this.queueStatus = await fetch('/api/status').then(r => r.json());
        this.resetCountdown();
      } catch (error) {
        console.error('获取队列状态失败:', error);
      }
    },
    async manualRefresh() {
      try {
        this.queueStatus = await fetch('/api/queue_status').then(r => r.json());
        this.resetCountdown();
      } catch (error) {
        console.error('获取队列状态失败:', error);
      }
    },
    resetCountdown() {
      this.refreshCountdown = this.refreshInterval;
    },

    startCountdown() {
      this.countdownTimer = setInterval(() => {
        if (this.refreshCountdown > 0) {
          this.refreshCountdown--;
        }
      }, 1000);
    },

    getCurrentTasksTooltip(processingTasks) {
      if (!processingTasks || processingTasks.length === 0) {
        return '当前没有正在处理的任务';
      }
      return `正在处理的任务:\n${processingTasks.join('\n')}`;
    },

    getStatusText(status) {
      const statusMap = {
        'waiting': '等待中',
        'processing': '处理中',
        'completed': '已完成',
        'failed': '失败',
        'paused': '已暂停',
        'canceled': '已取消',
        'delayed': '已延迟',
        'retry': '重试中',
        'unknown': '未知状态',
        'timeout': '超时',
        'online': '在线',
        'offline': '离线',
        'error': '错误',
        'initializing': '初始化中'
      };
      return statusMap[status] || '未知状态';
    },
    getStatusType(status) {
      const statusTypeMap = {
        'waiting': 'info',
        'processing': 'success',
        'completed': 'success',
        'failed': 'danger',
        'paused': 'warning',
        'canceled': 'info',
        'delayed': 'warning',
        'retry': 'warning',
        'unknown': 'danger',
        'timeout': 'danger',
        'online': 'success',
        'offline': 'danger',
        'error': 'danger',
        'initializing': 'warning'
      };
      return statusTypeMap[status] || 'danger';
    },
    getStatusColor(status) {
      const statusColorMap = {
        'waiting': '#909399',
        'processing': '#67c23a',
        'completed': '#67c23a',
        'failed': '#f56c6c',
        'paused': '#e6a23c',
        'canceled': '#909399',
        'delayed': '#e6a23c',
        'retry': '#e6a23c',
        'unknown': '#f56c6c',
        'online': '#67c23a',
        'offline': '#f56c6c',
        'error': '#f56c6c',
        'timeout': '#f56c6c',
        'initializing': '#e6a23c'
      };
      return statusColorMap[status] || '#f56c6c';
    },
    getStatusTextColor(status) {
      return '#fff';
    },

    // 解析状态相关方法
    getParseStatusText(status) {
      const statusMap = {
        'success': '正常',
        'failed': '失败',
        'timeout': '超时',
        'error': '错误',
        'unknown': '未知'
      };
      return statusMap[status] || '未知';
    },

    getParseStatusType(status) {
      const statusTypeMap = {
        'success': 'success',
        'failed': 'danger',
        'timeout': 'warning',
        'error': 'danger',
        'unknown': 'info'
      };
      return statusTypeMap[status] || 'info';
    },

    getParseStatusColor(status) {
      const statusColorMap = {
        'success': '#67c23a',
        'failed': '#f56c6c',
        'timeout': '#e6a23c',
        'error': '#f56c6c',
        'unknown': '#909399'
      };
      return statusColorMap[status] || '#909399';
    },

    getParseStatusTextColor(status) {
      return '#fff';
    },

    async testParseFunction(queue) {
      if (this.testingQueues.includes(queue) || this.cooldownQueues?.[queue]) return;

      this.testingQueues.push(queue);
      try {
        const response = await fetch(`/api/test_parse/${queue}`, {
          method: 'POST'
        });
        const result = await response.json();
        if (response.ok) {
          this.message = `解析功能测试已启动：${queue}`;
        } else {
          this.message = `测试启动失败：${result.error}`;
        }
      } catch (error) {
        this.message = `测试启动出错：${error.message}`;
      } finally {
        // 移除正在测试的队列
        const index = this.testingQueues.indexOf(queue);
        if (index > -1) {
          this.testingQueues.splice(index, 1);
        }
        // 添加冷却时间
        this.startCooldown(queue);
      }
    },

    startCooldown(queue) {
      if (!this.cooldownQueues) this.cooldownQueues = {};
      this.cooldownQueues[queue] = 10; // 10秒冷却

      const timer = setInterval(() => {
        if (this.cooldownQueues[queue] > 0) {
          this.cooldownQueues[queue]--;
        } else {
          clearInterval(timer);
          delete this.cooldownQueues[queue];
        }
      }, 1000);
    },

    getStatusTooltip(status) {
      const lines = [];
      if (status.last_check_time) {
        lines.push(`最后检查时间: ${new Date(status.last_check_time).toLocaleString()}`);
      }
      if (status.status === 'timeout' || status.status === 'offline' || status.error) {
        lines.push(`错误: ${status.error}`);
      }
      return lines.join('<br>');
    },

    getParseStatusTooltip(status) {
      const lines = [];
      if (status.last_parse_check_time) {
        lines.push(`最后解析检查时间: ${new Date(status.last_parse_check_time).toLocaleString()}`);
      }
      if (status.parse_error) {
        lines.push(`解析错误: ${status.parse_error}`);
      }
      return lines.join('<br>');
    },
  },
  mounted() {
    document.title = "文档解析队列查询";
    this.manualRefresh();
    this.timer = setInterval(this.refreshData, this.refreshInterval * 1000);
    this.startCountdown();
  },
  beforeUnmount() {
    clearInterval(this.timer);
    clearInterval(this.countdownTimer);
  }
}
</script>

<style>
.dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.status-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.refresh-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.countdown {
  font-size: 14px;
  color: #606266;
}

.current-task-item {
  min-width: 300px;
  width: 50%;
}

/* 控制描述列表内部布局 */
.el-descriptions__body .el-descriptions__table {
  table-layout: fixed;
}
</style>